import { FRONTEND_ERROR_API_NAME, FRONTEND_ERROR_CODE, TOKEN_EXPIRED } from './errors';
import type { BaseResponse, HttpClient, Result } from './HttpClient';
import { logApiError } from '../logging/errorLogger';
import { categorizeError, toAppError } from '../errors/errorCategories';
import { ErrorCategory } from '../errors/AppError';
import { startTransaction } from '../logging/sentry';
import { runSingleFlightRefresh } from './refreshCoordinator';

type APIRequest = {
  route: string;
  params: RequestInit;
};

export type QueueResponse = BaseResponse & {
  jobId: string; // Unique identifier for the message / jobId
  action: 'read' | 'write' | 'undefined'; // Action type
  status?: 'pending' | 'processing' | 'completed' | 'failed'; // Status of the message
};

export class ApiClient implements HttpClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly refreshToken: (() => Promise<void>) | null;
  private readonly onRefreshStateChange: ((isRefreshing: boolean) => void) | null;

  // Retry configuration
  private readonly MAX_RETRIES = 3;
  private readonly MAX_REFRESH_ATTEMPTS = 1;
  private readonly RETRY_DELAY_MS = 1000; // Base delay for exponential backoff

  private refreshAttempts = 0;

  constructor(
    baseUrl: string,
    getToken: () => string | null,
    refreshToken: (() => Promise<void>) | null,
    onRefreshStateChange: ((isRefreshing: boolean) => void) | null = null
  ) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
    this.refreshToken = refreshToken;
    this.onRefreshStateChange = onRefreshStateChange;
  }

  private buildHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Sleep for exponential backoff delay
   */
  private async sleep(retryCount: number): Promise<void> {
    const delay = this.RETRY_DELAY_MS * Math.pow(2, retryCount);
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  /**
   * Check if error should be retried based on status code
   */
  private shouldRetryRequest(statusCode: number): boolean {
    // Retry on: 408 (timeout), 429 (rate limit), 503 (unavailable), 504 (gateway timeout)
    return statusCode === 408 || statusCode === 429 || statusCode === 503 || statusCode === 504;
  }

  /**
   * Execute request with retry logic for transient errors
   */
  private async execWithRetry(request: APIRequest, retryCount = 0): Promise<Response> {
    try {
      const response = await this.exec(request);

      // If request failed with retryable error and we haven't exceeded max retries
      if (
        !response.ok &&
        this.shouldRetryRequest(response.status) &&
        retryCount < this.MAX_RETRIES
      ) {
        // Log retry attempt
        if (import.meta.env.DEV) {
          console.warn(
            `[ApiClient] Retrying request (attempt ${retryCount + 1}/${this.MAX_RETRIES}): ${request.route}`
          );
        }

        // Wait with exponential backoff
        await this.sleep(retryCount);

        // Retry the request
        return this.execWithRetry(request, retryCount + 1);
      }

      return response;
    } catch (error) {
      // Do not retry or log aborted requests — let AbortError propagate silently
      if (
        (error instanceof Error || error instanceof DOMException) &&
        error.name === 'AbortError'
      ) {
        throw error;
      }

      // Network errors: retry if we haven't exceeded max retries
      if (retryCount < this.MAX_RETRIES) {
        const category = categorizeError(error);

        // Only retry network and timeout errors
        if (category === ErrorCategory.NETWORK_ERROR || category === ErrorCategory.TIMEOUT_ERROR) {
          if (import.meta.env.DEV) {
            console.warn(
              `[ApiClient] Retrying after network error (attempt ${retryCount + 1}/${this.MAX_RETRIES}): ${request.route}`
            );
          }

          await this.sleep(retryCount);
          return this.execWithRetry(request, retryCount + 1);
        }
      }

      // Not logged here: `request()` logs every failure once, with its category and base URL.
      throw error;
    }
  }

  private async exec(request: APIRequest): Promise<Response> {
    const headers = this.buildHeaders();

    const fetchParams: RequestInit = {
      ...request.params,
      headers,
    };

    return await fetch(request.route, fetchParams);
  }

  private async handleReturn<T extends BaseResponse>(
    res: Response,
    request: APIRequest,
    refresh: boolean = true
  ): Promise<Result<T>> {
    let json: Record<string, unknown>;
    try {
      json = (await res.json()) as Record<string, unknown>;
    } catch {
      return {
        ok: false,
        code: res.status,
        name: 'ParseError',
        message: 'Non-JSON response from server',
        timestamp: new Date().toISOString(),
      };
    }

    if (res.ok) {
      // Reset refresh attempts on successful request
      this.refreshAttempts = 0;

      return {
        ok: true,
        data: json as T,
      };
    } else {
      // Handle token expiration with max refresh attempts
      if (
        json.code === 401 &&
        json.name === TOKEN_EXPIRED &&
        refresh &&
        this.refreshToken &&
        this.refreshAttempts < this.MAX_REFRESH_ATTEMPTS
      ) {
        this.refreshAttempts++;

        // Notify that refresh is starting
        this.onRefreshStateChange?.(true);

        try {
          if (import.meta.env.DEV) {
            console.log('[ApiClient] Awaiting token refresh (app-wide single flight)...');
          }

          // App-wide single flight: if any ApiClient instance already has a
          // refresh in flight, this awaits it instead of issuing another
          // /api/auth/refresh call (the backend rotates the refresh token on
          // every call, so parallel refreshes race and log the user out).
          await runSingleFlightRefresh(this.refreshToken);

          if (import.meta.env.DEV) {
            console.log('[ApiClient] Token refresh successful');
          }

          // Retry the original request
          const retry = await this.exec(request);
          return this.handleReturn(retry, request, false);
        } catch (refreshError) {
          // Token refresh failed
          this.refreshAttempts = 0;

          logApiError(refreshError, '/auth/refresh', 'POST', {
            component: 'ApiClient',
            action: 'refreshToken',
          });

          // Return auth error
          return {
            ok: false,
            code: 401,
            timestamp: new Date().toISOString(),
            name: 'AuthenticationError',
            message: 'errors.auth.sessionExpired',
          };
        } finally {
          // Reset refresh state
          this.onRefreshStateChange?.(false);
        }
      }

      // Narrow the untyped error body: the orchestrator's error envelope is
      // { code, name, message?, timestamp } but a proxy/gateway may return
      // anything, so fall back to the HTTP layer when fields are missing.
      const errorCode = typeof json.code === 'number' ? json.code : res.status;
      const errorName = typeof json.name === 'string' ? json.name : 'ApiError';
      // NestJS ValidationPipe sends `message` as a string[] (one entry per failed
      // rule, possibly repeated) — join it so forms can show the real reasons
      // instead of falling back to their generic error label.
      const errorMessage =
        typeof json.message === 'string'
          ? json.message
          : Array.isArray(json.message)
            ? [...new Set(json.message.filter((m): m is string => typeof m === 'string'))].join(
                '\n'
              ) || undefined
            : undefined;
      const errorTimestamp =
        typeof json.timestamp === 'string' ? json.timestamp : new Date().toISOString();

      // Log API error
      logApiError(
        new Error(errorMessage ?? `API error: ${errorCode}`),
        request.route,
        request.params.method || 'UNKNOWN',
        {
          component: 'ApiClient',
          extra: {
            statusCode: errorCode,
            errorName,
          },
        }
      );

      return {
        ok: false,
        code: errorCode,
        timestamp: errorTimestamp,
        name: errorName,
        ...(errorMessage !== undefined && { message: errorMessage }),
      };
    }
  }

  async get<T extends BaseResponse>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Result<T>> {
    return this.request<T>('GET', endpoint, options);
  }

  async post<T extends BaseResponse>(
    endpoint: string,
    data: unknown,
    options: RequestInit = {}
  ): Promise<Result<T>> {
    return this.request<T>('POST', endpoint, options, { data });
  }

  async put<T extends BaseResponse>(
    endpoint: string,
    data: unknown,
    options: RequestInit = {}
  ): Promise<Result<T>> {
    return this.request<T>('PUT', endpoint, options, { data });
  }

  async delete<T extends BaseResponse>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Result<T>> {
    return this.request<T>('DELETE', endpoint, options);
  }

  /**
   * The one request path behind every verb: performance transaction, retry-aware execution,
   * and the mapping of any thrown error onto a standardized `Result`. `payload` is an object
   * rather than a bare value so that an explicit `undefined`/`null` body stays distinguishable
   * from "this verb carries no body".
   */
  private async request<T extends BaseResponse>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    endpoint: string,
    options: RequestInit,
    payload?: { data: unknown }
  ): Promise<Result<T>> {
    // Start performance tracking for API call
    const transaction = startTransaction(`${method} ${endpoint}`, 'http.client');

    const request: APIRequest = {
      route: this.baseUrl + endpoint,
      params: {
        method,
        ...(payload && { body: JSON.stringify(payload.data) }),
        credentials: options.credentials ?? 'same-origin',
        ...(options.signal != null && { signal: options.signal }),
      },
    };

    try {
      const res = await this.execWithRetry(request);

      // Set HTTP status and end transaction
      if (transaction) {
        transaction.setData?.('http.status_code', res.status);
        transaction.setData?.('http.url', endpoint);
        transaction.end?.();
      }

      return this.handleReturn(res, request);
    } catch (err) {
      // Re-throw AbortError without logging — cancellation is intentional
      if ((err instanceof Error || err instanceof DOMException) && err.name === 'AbortError') {
        throw err;
      }

      // Mark transaction as failed
      if (transaction) {
        transaction.setStatus?.('internal_error');
        transaction.end?.();
      }

      // Categorize and log the error
      const appError = toAppError(err);
      const category = categorizeError(err);

      logApiError(appError, endpoint, method, {
        component: 'ApiClient',
        extra: {
          category,
          baseUrl: this.baseUrl,
          ...(payload && { hasData: !!payload.data }),
        },
      });

      // Return standardized error response
      return {
        ok: false,
        code: FRONTEND_ERROR_CODE,
        name: category || FRONTEND_ERROR_API_NAME,
        message: appError.message,
        timestamp: new Date().toISOString(),
      };
    }
  }
}
